<template>
  <div class="login-page">
    <b-container class="login-shell">
      <b-row class="login-frame no-gutters">
        <b-col lg="5" class="d-none d-lg-flex">
          <section class="brand-panel">
            <p class="eyebrow">HPPDM Workspace</p>
            <h1>Secure access for high-trust health project data.</h1>
            <p class="brand-copy">
              Sign in to manage submissions, track organizations, and move from review to
              decision-making in one refined workspace.
            </p>

            <div class="brand-metrics">
              <div class="metric-card">
                <span class="metric-value">Protected</span>
                <span class="metric-label">Role-based access and routed dashboards.</span>
              </div>
              <div class="metric-card">
                <span class="metric-value">Streamlined</span>
                <span class="metric-label">Fast transitions from login to active workflows.</span>
              </div>
            </div>
          </section>
        </b-col>

        <b-col cols="12" lg="7">
          <section class="form-panel">
            <div class="form-panel__inner">
              <div class="mobile-brand d-lg-none">
                <p class="eyebrow mb-2">HPPDM Workspace</p>
                <h1>Welcome back</h1>
              </div>

              <div class="form-header">
                <p class="form-kicker">Sign in</p>
                <h2>Access your account</h2>
                <p class="form-subtitle">
                  Use your email and password to continue to your dashboard.
                </p>
              </div>

              <b-alert
                :show="displaymodal"
                variant="warning"
                class="warningprompt"
                dismissible
                fade
                @dismissed="dismissAlert"
              >
                Please login to continue with dataset submission.
              </b-alert>

              <ValidationObserver v-slot="{ passes }">
                <form class="login-form" @submit.prevent="passes(LoginButton)">
                  <div v-if="loginError" class="login-error" role="alert">
                    <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                    <div>
                      <strong>Unable to sign in</strong>
                      <p>{{ loginError }}</p>
                    </div>
                  </div>
                  <base-input
                    v-model="email"
                    height="3.35rem"
                    placeholder="name@organization.org"
                    width="100%"
                    rules="required|email"
                    name="Email address"
                    label="Email address"
                    description="Enter the email tied to your HPPDM account."
                  />
                  <base-input
                    v-model="password"
                    height="3.35rem"
                    placeholder="Enter your password"
                    width="100%"
                    type="password"
                    rules="required"
                    name="Password"
                    label="Password"
                    description="Your credentials are encrypted and securely validated."
                  />

                  <div class="form-meta">
                    <label class="stay-signed-in">
                      <input v-model="currentValue" type="checkbox" />
                      <span>Stay signed in</span>
                    </label>

                    <b-link class="forgot-link text-decoration-none" to="/auth/reset-password">
                      Forgot password?
                    </b-link>
                  </div>

                  <button type="submit" class="login-button" :disabled="loading">
                    <b-spinner v-show="loading" small class="mr-2" />
                    <span>{{ loading ? 'Signing in...' : 'Login' }}</span>
                  </button>
                </form>
              </ValidationObserver>
            </div>
          </section>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  data() {
    return {
      type: 'user',
      password: '',
      email: '',
      loading: false,
      loginError: '',
      currentValue: '',
      user_type: ['user', 'organization', 'admin'],
      user: null,
      displaymodal: false,
      submitdatasetroute: null,
    };
  },

  mounted() {
    const authenticated = localStorage.getItem('vuex');
    const submitdataset = localStorage.getItem('submitdataroute');

    try {
      this.user = authenticated ? JSON.parse(authenticated) : null;
    } catch (error) {
      this.user = null;
    }

    this.submitdatasetroute = submitdataset;

    if (
      this.user &&
      this.user.auth &&
      this.user.auth.user === null &&
      this.submitdatasetroute === '/inventory/submit-dataset'
    ) {
      this.displaymodal = true;
    }

    setTimeout(() => {
      this.displaymodal = false;
    }, 20000);
  },

  watch: {
    email() {
      this.loginError = '';
    },
  },

  computed: {
    ...mapGetters('auth', ['role']),
  },

  methods: {
    ...mapActions('auth', ['LOGIN', 'REGISTRATION', 'SET_ROLE']),

    dismissAlert() {
      this.displaymodal = false;
    },

    getLoginErrorMessage(error) {
      const data = error.response?.data;
      const messages = (value) => {
        if (typeof value === 'string') return [value];
        if (Array.isArray(value)) return value.flatMap(messages);
        if (value && typeof value === 'object') {
          return [
            value.detail,
            value.message,
            value.error,
            value.non_field_errors,
            value.code,
          ].flatMap(messages);
        }
        return [];
      };
      const details = messages(data);
      const code = String(data?.code || '').toLowerCase();
      const disabledCodes = [
        'account_disabled',
        'user_disabled',
        'account_inactive',
        'user_inactive',
        'inactive_account',
        'disabled_account',
      ];
      const isDisabled =
        disabledCodes.includes(code) ||
        details.some(
          (message) =>
            disabledCodes.includes(message.toLowerCase()) ||
            /\b(account|user)\b.{0,40}\b(disabled|deactivated|inactive)\b|\b(disabled|deactivated|inactive)\b.{0,40}\b(account|user)\b/i.test(
              message
            )
        );
      if (isDisabled) {
        return 'Your account has been disabled. Please contact the administrator to restore access.';
      }
      if (!error.response) {
        return 'Unable to connect. Please check your connection and try again.';
      }
      if (error.response.status >= 500) {
        return 'Unable to sign in right now. Please try again later.';
      }
      const fieldErrors = messages(data?.non_field_errors);
      return (
        details[0] ||
        fieldErrors[0] ||
        'Unable to sign in. Please check your email and password and try again.'
      );
    },

    async LoginButton() {
      if (this.loading) return;
      this.loginError = '';
      this.loading = true;

      try {
        const data = await this.LOGIN({
          username: this.email,
          password: this.password,
          type: this.type,
        });

        if (data.status !== 200) {
          this.loginError = this.getLoginErrorMessage({ response: data });
          return;
        }

        localStorage.setItem('user', JSON.stringify(data.data.data));

        const user = data.data.data;
        this.$gates.setRoles([this.role]);

        if (Number(user.type) === 6) {
          this.$router.push('/admin/user');
        } else if (user.type === 0) {
          this.$router.push('/');
        } else if (user.type === 1) {
          this.$router.push('/admin/project');

          if (this.submitdatasetroute === '/inventory/submit-dataset') {
            this.$router.push('/inventory/submit-dataset');
          }
        } else if (user.type === 2) {
          this.$router.push('/admin/organization');
        } else if (user.type === 5) {
          this.$router.push('/admin/report');
        }
      } catch (error) {
        this.loginError = this.getLoginErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem 0;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.2), transparent 28%),
    radial-gradient(circle at bottom right, rgba(13, 34, 20, 0.18), transparent 30%),
    linear-gradient(135deg, #f3f8f3 0%, #edf3ee 45%, #e4ece5 100%);
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.26) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.26) 1px, transparent 1px);
  background-size: 120px 120px;
  opacity: 0.45;
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 4rem);
}

.login-frame {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 30px 90px rgba(20, 43, 27, 0.15);
  backdrop-filter: blur(14px);
}

.brand-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 3.5rem 3rem;
  color: #f8fff8;
  background: linear-gradient(180deg, rgba(5, 20, 10, 0.12), rgba(5, 20, 10, 0.2)),
    linear-gradient(155deg, #0d3418 0%, #195426 52%, #30ab4c 100%);
}

.brand-panel::after {
  content: '';
  position: absolute;
  width: 14rem;
  height: 14rem;
  right: -3rem;
  bottom: -4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(1px);
}

.eyebrow {
  margin-bottom: 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(248, 255, 248, 0.75);
}

.brand-panel h1,
.mobile-brand h1 {
  font-family: 'Open Sans', sans-serif;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.12;
  margin-bottom: 1rem;
}

.brand-copy {
  max-width: 28rem;
  margin-bottom: 2rem;
  font-size: 1rem;
  line-height: 1.75;
  color: rgba(248, 255, 248, 0.82);
}

.brand-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1.15rem 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.metric-value {
  margin-bottom: 0.35rem;
  font-size: 1rem;
  font-weight: 700;
}

.metric-label {
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(248, 255, 248, 0.74);
}

.form-panel {
  display: flex;
  align-items: center;
  min-height: 100%;
  padding: 2rem;
}

.form-panel__inner {
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  padding: 1rem;
}

.form-header {
  margin-bottom: 1rem;
}

.form-kicker {
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #30ab4c;
}

.form-header h2 {
  margin-bottom: 0.6rem;
  font-family: 'Open Sans', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #132218;
}

.form-subtitle {
  margin-bottom: 0;
  color: #56645b;
  line-height: 1.7;
}

.warningprompt {
  margin: 1.5rem 0 0.5rem;
  border: 0;
  border-radius: 16px;
  text-align: center;
  background: #fff3cd;
  color: #6f5200;
}

.login-form {
  margin-top: 0.75rem;
}
.login-error {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
  padding: 1rem 1.1rem;
  border: 1px solid #efd6d2;
  border-radius: 14px;
  background: #fff5f3;
  color: #943c32;
}
.login-error > i {
  margin-top: 0.2rem;
}
.login-error strong {
  font-size: 0.88rem;
}
.login-error p {
  margin: 0.3rem 0 0;
  font-size: 0.83rem;
  line-height: 1.65;
}

.login-form ::v-deep .inputBox {
  width: 100% !important;
}

.login-form ::v-deep .form-group {
  margin-top: 1.25rem !important;
  margin-bottom: 0.35rem !important;
}

.login-form ::v-deep .form-group > label,
.login-form ::v-deep .custom-control-label {
  font-size: 0.92rem;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  color: #1b2f21 !important;
}

.login-form ::v-deep .form-group p.text-black-50 {
  margin-bottom: 0.55rem !important;
  color: #6a776f !important;
  font-size: 0.88rem;
  line-height: 1.55;
}

.login-form ::v-deep .inputField {
  height: 3.35rem !important;
  padding: 0.85rem 1rem !important;
  border: 1px solid rgba(28, 68, 38, 0.16) !important;
  border-radius: 16px !important;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.96),
    rgba(243, 248, 244, 0.92)
  ) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 10px 24px rgba(21, 49, 29, 0.05);
  color: #132218 !important;
  font-size: 0.98rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease,
    transform 0.2s ease;
}

.login-form ::v-deep .inputField::placeholder {
  color: #8a958f;
}

.login-form ::v-deep .inputField:hover {
  border-color: rgba(48, 171, 76, 0.28) !important;
}

.login-form ::v-deep .inputField:focus {
  border-color: rgba(48, 171, 76, 0.7) !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.12), 0 16px 30px rgba(25, 84, 38, 0.1) !important;
  transform: translateY(-1px);
}

.login-form ::v-deep .password-icon {
  right: 1rem !important;
  bottom: 1rem !important;
  color: #6f7d75 !important;
  transition: color 0.2s ease, transform 0.2s ease;
}

.login-form ::v-deep .password-icon:hover {
  color: #1d6d31 !important;
  transform: scale(1.05);
}

.login-form ::v-deep .text-danger {
  margin-top: 0.45rem !important;
  color: #b53d3d !important;
}

.form-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.5rem 0;
}

.stay-signed-in {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  margin: 0;
  font-size: 0.95rem;
  color: #314339;
  cursor: pointer;
}

.stay-signed-in input {
  width: 1rem;
  height: 1rem;
  accent-color: #30ab4c;
}

.forgot-link {
  font-weight: 600;
  color: #1d6d31;
}

.forgot-link:hover {
  color: #144d22;
}

.login-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.5rem;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #16391f 0%, #226c34 45%, #30ab4c 100%);
  box-shadow: 0 18px 30px rgba(48, 171, 76, 0.24);
  color: #fff;
  font-size: 0.96rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-button:hover:not(:disabled),
.login-button:focus:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 22px 34px rgba(48, 171, 76, 0.28);
}

.login-button:disabled {
  opacity: 0.82;
  cursor: not-allowed;
}

.mobile-brand {
  margin-bottom: 1.5rem;
}

@media (max-width: 991px) {
  .login-page {
    padding: 1rem 0;
  }

  .login-shell {
    min-height: calc(100vh - 2rem);
  }

  .login-frame {
    border-radius: 22px;
  }

  .form-panel {
    padding: 1.25rem;
  }

  .form-panel__inner {
    padding: 0.5rem;
  }
}

@media (max-width: 575px) {
  .form-header h2,
  .mobile-brand h1 {
    font-size: 1.7rem;
  }

  .form-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .forgot-link {
    padding-left: 1.65rem;
  }
}
</style>
